import { Button, Datepicker, Dialog, Dropdown, DropdownItem, Flex, Loader, TextArea } from "@fluentui/react-northstar";
import React, { useContext, useEffect, useState } from "react";
import { RegularText } from "shared/components";
import { pl as plLocale } from "date-fns/locale";
import format from "date-fns/format";
import { constants } from "shared/constants";
import { AadUserDetails } from "modules/aad-user-details";
import { MsTeamsContext } from "msTeams/store";
import { ContactDetailsItem } from "../ContactDetailsItem";
import { dateLocalizationStrings, formatDate, formatDateTime } from "utils/formattingUtils";
import { INoteDetails, NoteDetails, NoteEditor, NoteSubject, NoteType } from "../../models";
import MultipleChoiceFilter from "shared/components/MultipleChoiceFilter/MultipleChoiceFilter";
import styles from "./noteDetailsForm.module.scss";
import { ResizerContext } from "shared/stores/resizeStore/ResizerContext";
import SubjectsDropdown from "./subjects-dropdown/SubjectsDropdown";
import { EntityNote_PublicEntityType, IntermediaryReference_Public } from "shared/service/api/generated";
import pl from "shared/messages/pl";

const renderDateAndAuthor = (isMobile: boolean, isMobileClient: boolean, date?: Date, user?: NoteEditor) => (
    <Flex gap="gap.small" column={isMobile}>
        <RegularText content={formatDateTime(date)} />
        {user && (
            <AadUserDetails
                fullName={user.fullName}
                email={user.userPrincipalName}
                phoneNumber={user.mobilePhone}
                mobileClient={isMobileClient}
            />
        )}
    </Flex>
);

export interface INoteDetailsFormReadOnly {
    isEditable: boolean;
    noteDetails: NoteDetails | undefined;
    onEditClick?: () => void;
    onNoteDeleted?: () => void;
}

export const NoteDetailsFormReadOnly: React.FC<INoteDetailsFormReadOnly> = ({
    noteDetails,
    isEditable,
    onEditClick,
    onNoteDeleted
}) => {
    const msTeamsContext = useContext(MsTeamsContext);
    const resizeContext = useContext(ResizerContext);

    const isMobile = resizeContext.windowWidth < constants.view360MobileDeviceWidthPx;

    const showEditButton = () => {
        return !isMobile && noteDetails?.canBeEdited && isEditable;
    }

    const showDeleteButton = () => {
        return !isMobile && noteDetails?.canBeDeleted && isEditable;
    }

    return (
        <Flex column gap="gap.large" style={{ display: undefined, height: "100%" }} className={isMobile ? styles.mobileNoteDetailsContainer : styles.noteDetailsContainer}>
            <Flex column gap="gap.medium" >
                <ContactDetailsItem
                    label={noteDetails?.entityType === EntityNote_PublicEntityType.INTERMEDIARY ? "Agencja" : "OFWCA"}
                    content={<RegularText content={noteDetails?.entityName ?? constants.noValue} />}
                />
                {noteDetails?.entityType === EntityNote_PublicEntityType.OFWCA && (
                    <ContactDetailsItem
                        label={pl.notes.details.intermediaryName}                                content={<RegularText content={noteDetails.intermediaryReferenceList.length !== 0
                            ? noteDetails?.intermediaryReferenceList.map(x => x.name).join(", ")
                            : constants.noValue} />}
                    />
                )}
                <ContactDetailsItem
                    label={pl.notes.details.createdAt}
                    content={renderDateAndAuthor(isMobile, msTeamsContext.isMobileClient, noteDetails?.createdAt, noteDetails?.createdBy)}
                />
                <ContactDetailsItem
                    label={pl.notes.details.modifiedAt}
                    content={renderDateAndAuthor(isMobile, msTeamsContext.isMobileClient, noteDetails?.modifiedAt, noteDetails?.modifiedBy)}
                />
                <ContactDetailsItem
                    label={pl.notes.details.type}
                    content={<RegularText content={noteDetails?.type.text ?? constants.noValue} />}
                />
                <ContactDetailsItem
                    label={pl.notes.details.contactDate}
                    content={<RegularText content={formatDate(noteDetails?.contactDate) ?? constants.noValue} />}
                />
                <ContactDetailsItem
                    label={pl.notes.details.topics}
                    content={<RegularText
                        content={noteDetails?.subjectRetailList?.length !== 0
                            ? noteDetails?.subjectRetailList?.map((subject) => subject.text).join(", ")
                            : constants.noValue}
                    />}
                />
                <ContactDetailsItem
                    label={pl.notes.details.noteBody}
                    content={<RegularText
                        content={noteDetails?.body ?? constants.noValue}
                    />} />
            </Flex>
            <Flex hAlign="end" gap="gap.medium">
                {showEditButton() && (
                    <Button
                        primary
                        content={pl.common.edit}
                        onClick={() => !!onEditClick ? onEditClick() : {}}
                    />
                )}
                {showDeleteButton() && (
                    <Dialog
                        cancelButton={pl.common.cancel}
                        confirmButton={pl.common.delete}
                        header={pl.notes.confirmNoteDeletion}
                        trigger={<Button secondary content={pl.common.delete} />}
                        onConfirm={() => !!onNoteDeleted ? onNoteDeleted() : {}}
                    />
                )}
            </Flex>
        </Flex>
    )
}

export interface NoteDetailsFormProps {
    salesPersonId?: number;
    selectedNoteId?: number;
    onNoteCreated: () => void;
    onNoteUpdated: () => void;
    refetchNoteQuery: () => void;
    noteDetails: NoteDetails | undefined;
    availableNoteTypes: NoteType[] | undefined;
    noteSubjectsRetailQuery: NoteSubject[] | undefined;
    loadingInProgress: boolean;
    editMode: boolean;
    editableNoteDetails: INoteDetails;
    entityName: string;
    intermediaryReference: IntermediaryReference_Public[];
    setEditableNoteDetails: (note: INoteDetails) => void;
    setEditMode: (editMode: boolean) => void;
}

const NoteDetailsForm: React.FC<NoteDetailsFormProps> = ({
    salesPersonId,
    selectedNoteId,
    onNoteCreated,
    onNoteUpdated,
    refetchNoteQuery,
    noteDetails,
    availableNoteTypes,
    noteSubjectsRetailQuery,
    loadingInProgress,
    editMode,
    editableNoteDetails,
    entityName,
    intermediaryReference,
    setEditableNoteDetails,
    setEditMode,
}) => {
    const msTeamsContext = useContext(MsTeamsContext);
    const resizeContext = useContext(ResizerContext);
    const isMobile = resizeContext.windowWidth < constants.view360MobileDeviceWidthPx;
    const [bodyError, setBodyError] = useState<string>();
    const [typeError, setTypeError] = useState<string>();
    const [intermediaryListError, setIntermediaryListError] = useState<string>();

    useEffect(() => {
        if (!selectedNoteId) {
            setEditMode(true);
        }
    }, [selectedNoteId]);

    const isNoteInvalid = () => {
        if(!editableNoteDetails.type) {
            setTypeError(pl.notes.errors.typeError);
        }
        if (editableNoteDetails.body.length === 0) {
            setBodyError(pl.notes.errors.bodyError);
        }
        if (intermediaryReference.length > 1 && editableNoteDetails.intermediaryReferenceList.length === 0) {
            setIntermediaryListError(pl.notes.errors.intermediaryListError);
        }
        return (!editableNoteDetails?.type
            || editableNoteDetails.body.length === 0
            || (intermediaryReference.length > 1 && editableNoteDetails.intermediaryReferenceList.length === 0))
    }

    const updateNote = () => {
        if (isNoteInvalid()) {
            return;
        }

        onNoteUpdated();
    }

    const intermediaryReferenceDropdown =
        intermediaryReference.length === 1
            ? <RegularText content={intermediaryReference[0].name ?? constants.noValue} />
            : <MultipleChoiceFilter
                availableChoices={intermediaryReference?.map((x) => ({ value: x!, valueAsString: x.name! })) ?? []}
                selectedChoices={editableNoteDetails.intermediaryReferenceList?.map((x) => ({ value: x!, valueAsString: x.name! })) ?? []}
                onSelectionChanged={(selectedItems: any) => {
                    let oldState = editableNoteDetails;
                    setEditableNoteDetails({...oldState, intermediaryReferenceList: selectedItems})}}
                notInverted
                placeholder="Wybierz agencje"
                disabled={loadingInProgress}
                className={styles.mulitpleDropdown}
                clearable
                isError={intermediaryListError !== undefined && editableNoteDetails.intermediaryReferenceList.length === 0}
            />
    ;

    const renderNoteTypeDropdown = () => {
        return (
            <Dropdown
                items={availableNoteTypes?.map((noteType) => (
                    <DropdownItem
                        key={noteType.code}
                        content={noteType.text}
                        selected={noteType.code === editableNoteDetails.type?.code}
                        checkable
                        onClick={() => {
                            let oldState = editableNoteDetails;
                            setEditableNoteDetails({...oldState, type: noteType})}}
                    />
                ))}
                value={editableNoteDetails.type?.text}
                disabled={loadingInProgress}
                fluid
                styles={{ width: "100%"}}
                placeholder={pl.notes.pickNoteType}
                noResultsMessage={pl.common.dropdownNoResultMessage}
                a11ySelectedItemsMessage={pl.common.dropdownDeleteMessage}
                error={typeError !== undefined && !editableNoteDetails.type}
            />)
    }

    const renderContactDatePicker = () => {
        return (
            <Datepicker
                inputOnly
                allowManualInput
                selectedDate={editableNoteDetails.contactDate}
                disabled={loadingInProgress}
                maxDate={new Date()}
                onDateChange={(_, data) => data?.value && setEditableNoteDetails({...editableNoteDetails, contactDate: data.value})}
                formatMonthDayYear={(date: Date, _) => format(date, constants.taskDetailsDateFormat, { locale: plLocale })}
                {...dateLocalizationStrings}
            />)
    }

    const renderSubjectsRetailDropdown = () =>{
        return(
            <SubjectsDropdown
                formDisabled={loadingInProgress}
                noteSubjectsList={noteSubjectsRetailQuery}
                selectedSubjects={editableNoteDetails.subjectRetailList}
                onSelectionChanged={(selectedItems) => {
                    let oldState = editableNoteDetails;
                    setEditableNoteDetails({...oldState, subjectRetailList: selectedItems as NoteSubject[]})}}
            />
    )}

    const renderBodyTextArea = () => {
        return (
            <TextArea
                fluid
                value={editableNoteDetails.body}
                disabled={loadingInProgress}
                onChange={(_, data) => {
                    let oldState = editableNoteDetails;
                    setEditableNoteDetails({...oldState, body: data?.value ?? ""})}}
                style={{ height: "100px" }}
                placeholder={pl.notes.addNoteContent}
                error={bodyError !== undefined && editableNoteDetails.body.length === 0}
            />);
    }

    return (
        <>
            <Loader label="Ładuje się" style={{ display: loadingInProgress ? undefined : "none" }} />
            <Flex column gap="gap.large" style={{ display: loadingInProgress ? "none" : undefined, height: "100%" }} className={isMobile ? styles.mobileNoteDetailsContainer : styles.noteDetailsContainer}>
                <Flex column gap="gap.medium" >
                    {selectedNoteId && (
                        <ContactDetailsItem
                            label={noteDetails?.entityType === EntityNote_PublicEntityType.INTERMEDIARY ? "Agencja" : "OFWCA"}
                            content={<RegularText content={entityName ?? constants.noValue} />}
                        />
                    )}
                    {(salesPersonId || noteDetails?.entityType === EntityNote_PublicEntityType.OFWCA) && (
                        <ContactDetailsItem
                            label={pl.notes.details.intermediaryName}
                            content={intermediaryReferenceDropdown}
                        />
                    )}
                    {selectedNoteId && (
                        <ContactDetailsItem
                            label={pl.notes.details.createdAt}
                            content={renderDateAndAuthor(isMobile, msTeamsContext.isMobileClient, noteDetails?.createdAt, noteDetails?.createdBy)}
                        />
                    )}
                    {selectedNoteId && (
                        <ContactDetailsItem
                            label={pl.notes.details.modifiedAt}
                            content={renderDateAndAuthor(isMobile, msTeamsContext.isMobileClient, noteDetails?.modifiedAt, noteDetails?.modifiedBy)}
                        />
                    )}
                    <ContactDetailsItem
                        label={pl.notes.details.type}
                        content={renderNoteTypeDropdown()} />
                    <ContactDetailsItem
                        label={pl.notes.details.contactDate}
                        content={renderContactDatePicker()} />
                    <ContactDetailsItem label={pl.notes.details.topics} content={renderSubjectsRetailDropdown()} />
                    <ContactDetailsItem label={pl.notes.details.noteBody} readonly content={renderBodyTextArea()} />
                </Flex>
                <Flex hAlign="end" gap="gap.medium">
                    {!noteDetails && (
                        <Button
                            primary
                            content={pl.common.add}
                            disabled={loadingInProgress}
                            onClick={() => {
                                if (isNoteInvalid()) {
                                    return;
                                }

                                onNoteCreated();
                            }}
                        />
                    )}
                    {!isMobile && noteDetails?.canBeEdited && (
                        <Button
                            primary
                            content={pl.common.save}
                            disabled={loadingInProgress}
                            onClick={() => updateNote()}
                        />
                    )}
                    {isMobile && editMode && selectedNoteId && (
                        <Button
                            primary
                            content={pl.common.save}
                            disabled={loadingInProgress}
                            onClick={() => updateNote()}
                        />
                    )}
                    {editMode && selectedNoteId && (
                        <Button
                            secondary
                            content={pl.common.cancel}
                            disabled={loadingInProgress}
                            onClick={() => {
                                refetchNoteQuery();
                                setEditMode(false);
                            }}
                        />
                    )}
                </Flex>
            </Flex>
        </>
    );
};

export default NoteDetailsForm;
